import type { CellClickedEvent } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  DWD_CHECK_STATUSES,
  type DwdCheckStatus,
  ONBOARDING_STATUSES,
  type OnboardingStatus,
} from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import {
  type CdwColDef,
  CdwSsrmGridComponent,
  type CdwValueFormatterParams,
  previewColumn,
} from '@cdw-ae/common-ui-components-search-grid';
import type {
  FilterBarField,
  SelectionOption,
} from '@cdw-ae/filter-bar-interfaces';
import {
  ContainsMatcher,
  FullMatchMatcher,
  IsMatcher,
  SelectFromMatcher,
  selectFromChipFormatter,
  selectFromToolTipFormatter,
  StartsWithMatcher,
} from '@cdw-ae/filter-bar-matcher-configs';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { CustomersDataService } from '../../../../common/services/customers-data/customers-data.service';
import { dwdStatusLabel } from '../../../../common/services/platform-customer/dwd-status';
import { onboardingStatusLabel } from '../../../../common/services/platform-customer/onboarding-status';

// Derive filter options from const arrays so new statuses gain their dropdown
// entry automatically.
const ONBOARDING_OPTIONS: SelectionOption[] = ONBOARDING_STATUSES.map(
  (status) => ({
    name: status,
    displayName: onboardingStatusLabel(status),
  })
);

const DWD_OPTIONS: SelectionOption[] = DWD_CHECK_STATUSES.map((status) => ({
  name: status,
  displayName: dwdStatusLabel(status),
}));

/**
 * Customers list — the SPOG SSRM grid (server-side paginate/sort/filter) with
 * its built-in filter bar, backed by POST /platform/customers/search. Read-only
 * for now; row navigation to a detail page is a later slice. The matcher modules
 * are imported so the filter-bar editors (text/selection/temporal) register.
 */
@Component({
  selector: 'cdw-customers-list',
  standalone: true,
  imports: [
    CommonModule,
    CdwButtonModule,
    CdwSsrmGridComponent,
    FilterBarTextMatchersModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
  ],
  templateUrl: './customers-list.component.html',
  styleUrl: './customers-list.component.scss',
})
export class CustomersListComponent {
  private readonly router = inject(Router);
  readonly customersDataService = inject(CustomersDataService);

  // `field` is the row property (camelCase). The filter-bar `name` (below) is the
  // DB column — kept separate on purpose (the server WHERE builder uses it raw).
  // The leading `previewColumn` is the eye/"View" icon → opens the detail page
  // via onCellClicked (same pattern as the device-search grid). Remaining fields
  // live on the detail page.
  readonly columnDefs: CdwColDef[] = [
    previewColumn,
    { field: 'id', headerName: 'CDW Customer ID', width: 150 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'primaryDomain', headerName: 'Primary Domain', flex: 1, minWidth: 170 },
    { field: 'schoolDistrict', headerName: 'School District', flex: 1, minWidth: 170 },
    {
      field: 'dwdCheckStatus',
      headerName: 'DWD',
      width: 150,
      valueFormatter: (params: CdwValueFormatterParams): string =>
        dwdStatusLabel(params.value as DwdCheckStatus | null),
    },
    {
      field: 'onboardingStatus',
      headerName: 'Onboarding',
      width: 140,
      valueFormatter: (params: CdwValueFormatterParams): string =>
        onboardingStatusLabel(params.value as OnboardingStatus | null),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 140,
      valueFormatter: (params: CdwValueFormatterParams): string =>
        params.value
          ? new Date(params.value as string).toLocaleDateString()
          : '',
    },
  ];

  readonly filterBarSettings: FilterBarField[] = [
    {
      displayName: 'Name',
      name: 'name',
      singleUse: true,
      fieldMatchers: [
        { ...ContainsMatcher, suggestMatcher: true },
        StartsWithMatcher,
        FullMatchMatcher,
      ],
    },
    {
      displayName: 'Primary Domain',
      name: 'primary_domain',
      singleUse: true,
      fieldMatchers: [
        { ...StartsWithMatcher, suggestMatcher: true },
        ContainsMatcher,
      ],
    },
    {
      displayName: 'Google ID',
      // camelCase unlike its neighbours: this column has no explicit @Column
      // name, so TypeORM named it after the property. 'google_id' 500s.
      name: 'googleId',
      singleUse: true,
      fieldMatchers: [{ ...FullMatchMatcher, suggestMatcher: true }],
    },
    {
      displayName: 'Onboarding',
      name: 'onboarding_status',
      singleUse: true,
      fieldMatchers: [
        {
          ...SelectFromMatcher,
          suggestMatcher: true,
          editorOptions: {
            selectionOptions: ONBOARDING_OPTIONS,
          },
          options: {
            chipFormatter: (value: string): string =>
              selectFromChipFormatter(value, ONBOARDING_OPTIONS),
            toolTipFormatter: (value: string): string =>
              selectFromToolTipFormatter(value, ONBOARDING_OPTIONS),
          },
        },
      ],
    },
    {
      displayName: 'DWD',
      name: 'dwd_check_status',
      // singleUse: false so the two matchers below can be applied together — the
      // server ORs same-field filters, giving "any failure status OR never
      // checked" in one query. Onboarding keeps singleUse: true (non-null).
      singleUse: false,
      fieldMatchers: [
        {
          ...SelectFromMatcher,
          suggestMatcher: true,
          editorOptions: {
            selectionOptions: DWD_OPTIONS,
          },
          options: {
            chipFormatter: (value: string): string =>
              selectFromChipFormatter(value, DWD_OPTIONS),
            toolTipFormatter: (value: string): string =>
              selectFromToolTipFormatter(value, DWD_OPTIONS),
          },
        },
        // The NULL path: selectFrom compiles to IN(...), which never matches
        // NULL, so "never checked" needs `is` + the matcher's default
        // { name: 'missing' } option, which the server maps to IsNull().
        //
        // ⚠️ CURRENTLY UNREACHABLE IN THE UI — do not assume this works. The
        // shared filter bar only offers a field's FIRST matcher or one flagged
        // suggestMatcher (filter-bar.component.ts:344 + the autocomplete
        // template), and clicking a chip re-opens the same matcher rather than
        // offering a switcher. So this is correct-but-inert config, kept
        // because it starts working the moment that gap is closed. The same
        // applies to `is` on device search's Auto Update Expiry field.
        // Tracked as EDUS-2846 — recheck once the in-flight branches merge.
        // Meanwhile: sort the DWD column DESC — Postgres puts NULLs first.
        IsMatcher,
      ],
    },
  ] as FilterBarField[];

  onboardNew(): void {
    this.router.navigate(['/customers/new']);
  }

  /** Only the eye/"View" icon navigates to the detail page (like device-search). */
  onCellClicked(event: CellClickedEvent): void {
    if (event.column?.getColId() !== 'previewIconComponent') {
      return;
    }
    const id = (event.data as { id?: number } | undefined)?.id;
    if (id == null) {
      return;
    }
    this.router.navigate(['/customers', id]);
  }
}
